import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tabs, Tab, List, ListItemButton, ListItemIcon, ListItemText,
  Checkbox, IconButton, Box, Typography, CircularProgress, Breadcrumbs, Link,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useTranslation } from 'react-i18next';
import myDriveService, { DirCategory, DriveFile }
  from '../../pages/ee/myDrive/services/myDriveService';

/**
 * 共用檔案選擇器（my_drive object storage）。
 * Phase 4 ee_project_management 也會用 → 走 props 控制單/多選 + 可選 category。
 */
export interface FilePickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (fileIds: number[]) => void;       // 單選回 [id]，多選回 list
  multiSelect?: boolean;                       // 預設 false
  allowedCategories?: DirCategory[];           // 預設全 4 個
  title?: string;
}

const ALL_CATEGORIES: DirCategory[] = ['personal', 'photos', 'system', 'mail'];

export default function FilePicker({
  open, onClose, onPick, multiSelect = false, allowedCategories, title,
}: FilePickerProps) {
  const { t } = useTranslation();
  const cats = allowedCategories && allowedCategories.length ? allowedCategories : ALL_CATEGORIES;

  const [category, setCategory] = useState<DirCategory>(cats[0]);
  const [prefix, setPrefix]     = useState('');
  const [items, setItems]       = useState<DriveFile[]>([]);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    myDriveService.listFiles(category, prefix)
      .then(data => setItems(data.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [category, prefix]);

  useEffect(() => {
    if (!open) { setSelected(new Set()); setPrefix(''); return; }
    load();
  }, [open, load]);

  // 只顯示當前 prefix 的直接子代
  const directItems = items.filter(f => {
    const remaining = f.relative_path.slice(prefix.length);
    return remaining.length > 0 && !remaining.includes('/');
  });

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = multiSelect ? new Set(prev) : new Set<number>();
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const enterFolder = (f: DriveFile) => { setPrefix(f.relative_path + '/'); setSelected(new Set()); };
  const goUp = () => {
    const parts = prefix.replace(/\/$/, '').split('/');
    parts.pop();
    setPrefix(parts.length ? parts.join('/') + '/' : '');
  };

  const breadcrumbParts = prefix ? prefix.replace(/\/$/, '').split('/') : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title || t('myDrive.title', '我的網路硬碟')}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs
          value={category}
          onChange={(_, v) => { setCategory(v); setPrefix(''); setSelected(new Set()); }}
          variant="scrollable" scrollButtons="auto"
        >
          {cats.map(c => (
            <Tab key={c} value={c} label={t(`myDrive.cat${c.charAt(0).toUpperCase() + c.slice(1)}`, c)} />
          ))}
        </Tabs>

        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <IconButton size="small" onClick={goUp} disabled={!prefix}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="›">
            <Link component="button" variant="body2" underline="hover" onClick={() => setPrefix('')}>
              {t(`myDrive.cat${category.charAt(0).toUpperCase() + category.slice(1)}`, category)}
            </Link>
            {breadcrumbParts.map((part, idx) => {
              const path = breadcrumbParts.slice(0, idx + 1).join('/') + '/';
              const isLast = idx === breadcrumbParts.length - 1;
              return isLast
                ? <Typography key={path} variant="body2">{part}</Typography>
                : <Link key={path} component="button" variant="body2" underline="hover"
                        onClick={() => setPrefix(path)}>{part}</Link>;
            })}
          </Breadcrumbs>
        </Box>

        <Box sx={{ minHeight: 240, maxHeight: 360, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : directItems.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 4 }}>
              {t('myDrive.empty', '（無檔案）')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {directItems.map(f => (
                <ListItemButton
                  key={f.id}
                  onClick={() => f.is_directory ? enterFolder(f) : toggle(f.id)}
                  selected={selected.has(f.id)}
                >
                  {!f.is_directory && (
                    <Checkbox edge="start" size="small" tabIndex={-1}
                      checked={selected.has(f.id)} onClick={e => { e.stopPropagation(); toggle(f.id); }} />
                  )}
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {f.is_directory ? <FolderIcon fontSize="small" color="warning" />
                                    : <InsertDriveFileIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={f.filename}
                    secondary={f.is_directory ? undefined : f.mime_type}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', '取消')}</Button>
        <Button variant="contained" disabled={selected.size === 0}
                onClick={() => onPick(Array.from(selected))}>
          {t('common.confirm', '確認')}{selected.size > 0 ? ` (${selected.size})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
